import { t } from "@/lib/i18n/interpolate";
import {
  asSentence,
  capitalizeFirst,
  stripFinalPeriod,
} from "@/lib/tool-kit/publishing/authors";
import { formatIsoDate } from "@/lib/tool-kit/publishing/dates";

/**
 * Data and code availability statements.
 *
 * The scenarios follow the categories the large publishers' policies share — Wiley's
 * sample statements, Springer Nature's examples and PLOS's data policy all sort data
 * into: in a repository (with a DOI or accession number), reused from public sources,
 * restricted (privacy, ethics, a third party's license), available on request, in the
 * article or its supplement, or none generated. A paper can be in several at once —
 * survey responses in a repository, interview transcripts restricted — so scenarios
 * combine, except "no data", which stands alone.
 *
 * Sentences come out in a fixed order, open data first and "in the article" last,
 * because that is the order in which "Other data … are available within the article"
 * reads correctly; on its own the article sentence says "All data".
 */

export const DATA_SCENARIO_IDS = [
  "repository",
  "public",
  "restricted",
  "request",
  "article",
  "none",
] as const;

export type DataScenarioId = (typeof DATA_SCENARIO_IDS)[number];

export type RestrictionReason = "privacy" | "ethics" | "thirdParty" | "legal";
export type NoDataReason = "noData" | "theoretical";
export type CodeMode = "off" | "repository" | "request" | "none";

export interface RepositoryInput {
  repository: string;
  /** A DOI, an accession number or a URL. */
  identifier: string;
  /** What this deposit holds, when there is more than one. */
  description: string;
  license: string;
  /** "YYYY-MM-DD" when the files are embargoed until then. */
  embargo: string;
}

export interface DataInputs {
  scenarios: readonly DataScenarioId[];
  supplementary: boolean;
  files: string;
  repositories: readonly RepositoryInput[];
  publicSources: string;
  requestContact: string;
  requestReason: string;
  restriction: RestrictionReason;
  /** What is restricted — "interview transcripts". Empty means all the data. */
  restrictedDescription: string;
  party: string;
  accessContact: string;
  accessDetails: string;
  conditions: string;
  noDataReason: NoDataReason;
}

export interface CodeInputs {
  mode: CodeMode;
  repository: string;
  identifier: string;
  description: string;
  license: string;
  contact: string;
  software: string;
}

export interface DataTemplates {
  scopeAll: string;
  scopeOther: string;
  articleOnly: string;
  articleSupplement: string;
  articleSupplementFiles: string;
  described: string;
  whereLink: string;
  whereAccession: string;
  repositoryOpen: string;
  repositoryEmbargo: string;
  repositoryLicense: string;
  publicData: string;
  requestReason: string;
  request: string;
  correspondingAuthor: string;
  restrictedSubjectAll: string;
  restrictedSubject: string;
  thirdPartySubjectAll: string;
  restrictedPrivacy: string;
  restrictedEthics: string;
  restrictedLegal: string;
  restrictedThirdParty: string;
  restrictedAccess: string;
  contactDetails: string;
  restrictedConditions: string;
  noneNoData: string;
  noneTheoretical: string;
  codeOpen: string;
  codeLicense: string;
  codeRequest: string;
  codeNone: string;
  codeNoneSoftware: string;
  placeholderRepository: string;
  placeholderIdentifier: string;
  placeholderSources: string;
  placeholderParty: string;
  placeholderContact: string;
  placeholderDate: string;
}

/* ---------------------------------------------------------------- identifiers */

const RE_DOI_PREFIX = /^(?:https?:\/\/)?(?:dx\.)?doi\.org\//i;
const RE_DOI_SCHEME = /^doi:\s*/i;
const RE_DOI = /^10\.\d{4,9}\/\S+$/;
const RE_URL = /^https?:\/\/\S+$/i;
const RE_LOOKS_LIKE_DOI = /^(?:doi:|10\.|(?:https?:\/\/)?(?:dx\.)?doi\.org)/i;
const RE_CODE_HOST = /\b(?:github\.com|gitlab\.com|bitbucket\.org)\b/i;

/** "doi:10.5281/zenodo.123", "https://doi.org/10.5281/zenodo.123" → "10.5281/zenodo.123" */
export function normalizeDoi(raw: string): string | null {
  const bare = raw.trim().replace(RE_DOI_PREFIX, "").replace(RE_DOI_SCHEME, "");
  return RE_DOI.test(bare) ? bare : null;
}

export type IdentifierKind = "doi" | "url" | "accession" | "empty";

export interface Identifier {
  kind: IdentifierKind;
  /** A DOI as its https://doi.org/ link, a URL as typed, an accession number as typed. */
  value: string;
}

export function classifyIdentifier(raw: string): Identifier {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return { kind: "empty", value: "" };
  }
  const doi = normalizeDoi(trimmed);
  if (doi) {
    return { kind: "doi", value: `https://doi.org/${doi}` };
  }
  if (RE_URL.test(trimmed)) {
    return { kind: "url", value: trimmed };
  }
  return { kind: "accession", value: trimmed };
}

/** Typed as a DOI but not a complete one — "10.5281/" or "doi.org/" with nothing after. */
export function isIncompleteDoi(raw: string): boolean {
  const trimmed = raw.trim();
  return (
    trimmed !== "" && RE_LOOKS_LIKE_DOI.test(trimmed) && !normalizeDoi(trimmed)
  );
}

/** A link to a code-hosting site, which can change or disappear. */
export function isCodeHostLink(raw: string): boolean {
  return RE_CODE_HOST.test(raw);
}

/* ------------------------------------------------------------------ sentences */

function orPlaceholder(value: string, placeholder: string): string {
  const trimmed = stripFinalPeriod(value);
  return trimmed === "" ? placeholder : trimmed;
}

function where(
  repository: string,
  identifier: Identifier,
  templates: DataTemplates
): string {
  const name = orPlaceholder(repository, templates.placeholderRepository);
  if (identifier.kind === "accession") {
    return t(templates.whereAccession, {
      repository: name,
      accession: identifier.value,
    });
  }
  return t(templates.whereLink, {
    repository: name,
    link: identifier.value || templates.placeholderIdentifier,
  });
}

/** " (survey responses)" — what a deposit holds, when the writer says. */
function described(description: string, templates: DataTemplates): string {
  const trimmed = stripFinalPeriod(description);
  return trimmed === "" ? "" : t(templates.described, { description: trimmed });
}

function repositorySentences(
  entry: RepositoryInput,
  templates: DataTemplates,
  locale: string
): string[] {
  const location = where(
    entry.repository,
    classifyIdentifier(entry.identifier),
    templates
  );
  const subject = described(entry.description, templates);
  const sentences: string[] = [];
  if (entry.embargo.trim() === "") {
    sentences.push(
      t(templates.repositoryOpen, { described: subject, where: location })
    );
  } else {
    sentences.push(
      t(templates.repositoryEmbargo, {
        described: subject,
        where: location,
        date: formatIsoDate(entry.embargo, locale) ?? templates.placeholderDate,
      })
    );
  }
  const license = stripFinalPeriod(entry.license);
  if (license !== "") {
    sentences.push(t(templates.repositoryLicense, { license }));
  }
  return sentences;
}

function articleSentence(
  inputs: DataInputs,
  templates: DataTemplates,
  alone: boolean
): string {
  const scope = alone ? templates.scopeAll : templates.scopeOther;
  if (!inputs.supplementary) {
    return t(templates.articleOnly, { scope });
  }
  const files = stripFinalPeriod(inputs.files);
  return files === ""
    ? t(templates.articleSupplement, { scope })
    : t(templates.articleSupplementFiles, { scope, files });
}

function requestSentences(
  inputs: DataInputs,
  templates: DataTemplates
): string[] {
  const sentences: string[] = [];
  const reason = stripFinalPeriod(inputs.requestReason);
  if (reason !== "") {
    sentences.push(t(templates.requestReason, { reason }));
  }
  sentences.push(
    t(templates.request, {
      contact: orPlaceholder(
        inputs.requestContact,
        templates.correspondingAuthor
      ),
    })
  );
  return sentences;
}

const RESTRICTION_TEMPLATE: Record<
  Exclude<RestrictionReason, "thirdParty">,
  keyof DataTemplates
> = {
  privacy: "restrictedPrivacy",
  ethics: "restrictedEthics",
  legal: "restrictedLegal",
};

function restrictionSentence(
  inputs: DataInputs,
  templates: DataTemplates
): string {
  const description = stripFinalPeriod(inputs.restrictedDescription);
  if (inputs.restriction === "thirdParty") {
    return t(templates.restrictedThirdParty, {
      subject:
        description === "" ? templates.thirdPartySubjectAll : description,
      party: orPlaceholder(inputs.party, templates.placeholderParty),
    });
  }
  const subject =
    description === ""
      ? templates.restrictedSubjectAll
      : t(templates.restrictedSubject, { description });
  return t(templates[RESTRICTION_TEMPLATE[inputs.restriction]], { subject });
}

function restrictedSentences(
  inputs: DataInputs,
  templates: DataTemplates
): string[] {
  const fallbackContact =
    inputs.restriction === "thirdParty" ? inputs.party : "";
  const details = stripFinalPeriod(inputs.accessDetails);
  const sentences = [
    restrictionSentence(inputs, templates),
    t(templates.restrictedAccess, {
      contact: orPlaceholder(
        inputs.accessContact.trim() || fallbackContact,
        templates.placeholderContact
      ),
      details: details === "" ? "" : t(templates.contactDetails, { details }),
    }),
  ];
  const conditions = stripFinalPeriod(inputs.conditions);
  if (conditions !== "") {
    sentences.push(t(templates.restrictedConditions, { conditions }));
  }
  return sentences;
}

function scenarioSentences(
  scenario: DataScenarioId,
  inputs: DataInputs,
  templates: DataTemplates,
  context: { locale: string; alone: boolean }
): string[] {
  switch (scenario) {
    case "repository":
      return inputs.repositories.flatMap((entry) =>
        repositorySentences(entry, templates, context.locale)
      );
    case "public":
      return [
        t(templates.publicData, {
          sources: orPlaceholder(
            inputs.publicSources,
            templates.placeholderSources
          ),
        }),
      ];
    case "restricted":
      return restrictedSentences(inputs, templates);
    case "request":
      return requestSentences(inputs, templates);
    case "article":
      return [articleSentence(inputs, templates, context.alone)];
    default:
      return [
        inputs.noDataReason === "theoretical"
          ? templates.noneTheoretical
          : templates.noneNoData,
      ];
  }
}

/** Ticking "no data" clears the rest; ticking anything else clears "no data". */
export function toggleScenario(
  current: readonly DataScenarioId[],
  id: DataScenarioId,
  on: boolean
): DataScenarioId[] {
  if (!on) {
    return current.filter((item) => item !== id);
  }
  if (id === "none") {
    return ["none"];
  }
  return [...current.filter((item) => item !== "none"), id];
}

/** The scenarios that will be written, in output order. "None" stands alone. */
export function activeScenarios(
  scenarios: readonly DataScenarioId[]
): DataScenarioId[] {
  if (scenarios.includes("none")) {
    return ["none"];
  }
  return DATA_SCENARIO_IDS.filter((id) => scenarios.includes(id));
}

function finish(sentences: readonly string[]): string {
  return sentences
    .map((sentence) => capitalizeFirst(asSentence(sentence)))
    .filter((sentence) => sentence !== "")
    .join(" ");
}

/** The data availability statement: every selected scenario, in a fixed order. */
export function dataAvailabilityStatement(
  inputs: DataInputs,
  templates: DataTemplates,
  locale: string
): string {
  const chosen = activeScenarios(inputs.scenarios);
  const context = { locale, alone: chosen.length === 1 };
  return finish(
    chosen.flatMap((scenario) =>
      scenarioSentences(scenario, inputs, templates, context)
    )
  );
}

/** The code availability statement, or "" when the writer has not asked for one. */
export function codeAvailabilityStatement(
  code: CodeInputs,
  templates: DataTemplates
): string {
  const subject = described(code.description, templates);
  if (code.mode === "repository") {
    const license = stripFinalPeriod(code.license);
    return finish([
      t(templates.codeOpen, {
        described: subject,
        where: where(
          code.repository,
          classifyIdentifier(code.identifier),
          templates
        ),
      }),
      license === "" ? "" : t(templates.codeLicense, { license }),
    ]);
  }
  if (code.mode === "request") {
    return finish([
      t(templates.codeRequest, {
        described: subject,
        contact: orPlaceholder(code.contact, templates.correspondingAuthor),
      }),
    ]);
  }
  if (code.mode === "none") {
    const software = stripFinalPeriod(code.software);
    return finish([
      software === ""
        ? templates.codeNone
        : t(templates.codeNoneSoftware, { software }),
    ]);
  }
  return "";
}
